import { lookAt, multiply, perspective, type Mat4, type Vec3 } from './mat4.ts';

/**
 * Orbit camera driven by pointer events.
 *
 * Pointer events rather than separate mouse and touch paths, so a phone gets
 * drag-to-orbit and pinch-to-zoom from the same code that gives a laptop
 * drag-and-wheel - which matters, because the phone is the device this whole
 * application is pointed at.
 */

export interface OrbitOptions {
  distance: number;
  minDistance: number;
  maxDistance: number;
  /** Height of the point being orbited, in metres. */
  targetY?: number;
}

export class OrbitCamera {
  azimuth = Math.PI * 0.28;
  elevation = 0.42;
  distance: number;
  targetY: number;

  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinchDistance = 0;
  private readonly detachers: (() => void)[] = [];

  private readonly element: HTMLElement;
  private readonly options: OrbitOptions;
  private readonly onChange: () => void;

  constructor(element: HTMLElement, options: OrbitOptions, onChange: () => void) {
    this.element = element;
    this.options = options;
    this.onChange = onChange;
    this.distance = options.distance;
    this.targetY = options.targetY ?? 0;
    this.attach();
  }

  /** True once the user has moved the camera themselves. */
  touched = false;

  private attach(): void {
    const el = this.element;

    const down = (event: PointerEvent): void => {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      el.setPointerCapture(event.pointerId);
      if (this.pointers.size === 1) {
        this.dragging = true;
        this.lastX = event.clientX;
        this.lastY = event.clientY;
      } else if (this.pointers.size === 2) {
        this.pinchDistance = this.spread();
      }
    };

    const move = (event: PointerEvent): void => {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size >= 2) {
        const spread = this.spread();
        if (this.pinchDistance > 0 && spread > 0) {
          this.zoom(this.pinchDistance / spread);
          this.pinchDistance = spread;
        }
        return;
      }

      if (!this.dragging) return;
      const dx = event.clientX - this.lastX;
      const dy = event.clientY - this.lastY;
      this.lastX = event.clientX;
      this.lastY = event.clientY;

      this.azimuth -= dx * 0.007;
      // Stop just short of the poles, where the up vector degenerates.
      this.elevation = clamp(this.elevation + dy * 0.007, -1.45, 1.45);
      this.touched = true;
      this.onChange();
    };

    const up = (event: PointerEvent): void => {
      this.pointers.delete(event.pointerId);
      if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
      if (this.pointers.size < 2) this.pinchDistance = 0;
      if (this.pointers.size === 0) this.dragging = false;
    };

    const wheel = (event: WheelEvent): void => {
      event.preventDefault();
      this.zoom(Math.exp(event.deltaY * 0.0012));
    };

    const keys = (event: KeyboardEvent): void => {
      const step = event.shiftKey ? 0.22 : 0.08;
      if (event.key === 'ArrowLeft') this.azimuth += step;
      else if (event.key === 'ArrowRight') this.azimuth -= step;
      else if (event.key === 'ArrowUp') this.elevation = clamp(this.elevation + step, -1.45, 1.45);
      else if (event.key === 'ArrowDown') this.elevation = clamp(this.elevation - step, -1.45, 1.45);
      else if (event.key === '+' || event.key === '=') this.zoom(0.9);
      else if (event.key === '-' || event.key === '_') this.zoom(1.1);
      else return;
      event.preventDefault();
      this.touched = true;
      this.onChange();
    };

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('wheel', wheel, { passive: false });
    el.addEventListener('keydown', keys);

    this.detachers.push(() => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('wheel', wheel);
      el.removeEventListener('keydown', keys);
    });
  }

  private spread(): number {
    const points = [...this.pointers.values()];
    if (points.length < 2) return 0;
    return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
  }

  private zoom(factor: number): void {
    this.distance = clamp(this.distance * factor, this.options.minDistance, this.options.maxDistance);
    this.touched = true;
    this.onChange();
  }

  /** Re-frame for a new scene, keeping the angle the user chose. */
  reframe(distance: number, bounds: { min: number; max: number }, targetY: number): void {
    this.options.minDistance = bounds.min;
    this.options.maxDistance = bounds.max;
    this.targetY = targetY;
    if (!this.touched) this.distance = distance;
    else this.distance = clamp(this.distance, bounds.min, bounds.max);
  }

  eye(): Vec3 {
    const horizontal = Math.cos(this.elevation) * this.distance;
    return [
      Math.sin(this.azimuth) * horizontal,
      Math.sin(this.elevation) * this.distance + this.targetY,
      Math.cos(this.azimuth) * horizontal,
    ];
  }

  viewProjection(aspect: number): Mat4 {
    const near = Math.max(0.05, this.distance * 0.02);
    const far = this.distance * 12 + 40;
    return multiply(
      perspective(Math.PI / 4.4, aspect, near, far),
      lookAt(this.eye(), [0, this.targetY, 0], [0, 1, 0]),
    );
  }

  destroy(): void {
    for (const detach of this.detachers) detach();
    this.detachers.length = 0;
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
